'use client';

/**
 * MagicSelect Component
 *
 * A select component with animated focus glow and glassmorphism dropdown.
 * Automatically adapts to light/dark theme.
 */

import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { type SelectProps } from '@mui/material/Select';
import { alpha, styled, useTheme } from '@mui/material/styles';
import { forwardRef, useId } from 'react';
import { glassEffects, glowEffects } from '../../theme/shadows';
import { transitionPresets } from '../../theme/transitions';
import { transientOptions } from '../../utils/styledUtils';

// ============================================================================
// Types
// ============================================================================

export interface MagicSelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

export interface MagicSelectProps extends Omit<SelectProps, 'variant'> {
  /**
   * Select options
   */
  options: MagicSelectOption[];

  /**
   * Helper text to display below the select
   */
  helperText?: string;

  /**
   * Glow color variant
   * @default 'primary'
   */
  glowColor?: 'primary' | 'secondary' | 'mixed';

  /**
   * Enable glassmorphism effect on dropdown
   * @default true
   */
  glassDropdown?: boolean;

  /**
   * Select variant (always outlined for design consistency)
   * @default 'outlined'
   */
  variant?: 'outlined';
}

// ============================================================================
// Styled Components
// ============================================================================

interface StyledFormControlProps {
  $isDark: boolean;
  $glowColor: 'primary' | 'secondary' | 'mixed';
  $error?: boolean;
  $focused?: boolean;
}

const getGlowValue = (color: 'primary' | 'secondary' | 'mixed', intensity: 'subtle' | 'medium'): string =>
  glowEffects[color][intensity];

const StyledFormControl = styled(
  FormControl,
  transientOptions,
)<StyledFormControlProps>(({ theme, $isDark, $glowColor, $error }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;

  return {
    '& .MuiOutlinedInput-root': {
      transition: transitionPresets.input,
      borderRadius: 8,
      height: 46,
      background: glass.background,
      backdropFilter: glass.backdropBlur,
      fontSize: '0.875rem',

      '& .MuiSelect-select.MuiSelect-outlined': {
        padding: '11.5px 16px',
        minHeight: 'unset',
        display: 'flex',
        alignItems: 'center',
      },

      '& fieldset': {
        borderColor: $error ? theme.palette.error.main : glass.border,
        transition: transitionPresets.input,
      },

      '&:hover fieldset': {
        borderColor: $error ? theme.palette.error.main : glass.borderHover,
      },

      '&.Mui-focused': {
        '& fieldset': {
          borderColor: $error
            ? theme.palette.error.main
            : theme.palette[$glowColor === 'mixed' ? 'primary' : $glowColor].main,
          borderWidth: 2,
        },
        boxShadow: $error ? `0 0 0 3px ${alpha(theme.palette.error.main, 0.2)}` : getGlowValue($glowColor, 'medium'),
      },
    },

    '& .MuiInputLabel-root': {
      color: theme.palette.text.secondary,
      fontSize: '0.875rem',

      // Add background when label is shrunk (floating on border)
      '&.MuiInputLabel-shrink': {
        backgroundColor: $isDark ? '#1a1a1a' : '#ffffff',
        padding: '0 6px',
        marginLeft: '-4px',
      },

      '&.Mui-focused': {
        color: $error ? theme.palette.error.main : theme.palette[$glowColor === 'mixed' ? 'primary' : $glowColor].main,
      },
    },
  };
});

const StyledMenuItem = styled(
  MenuItem,
  transientOptions,
)<{ $isDark: boolean }>(({ theme, $isDark }) => ({
  borderRadius: 8,
  margin: theme.spacing(0.5, 1),
  fontSize: '0.875rem',
  transition: transitionPresets.button,

  '&:hover': {
    backgroundColor: $isDark ? alpha(theme.palette.primary.main, 0.15) : alpha(theme.palette.primary.main, 0.1),
  },

  '&.Mui-selected': {
    backgroundColor: $isDark ? alpha(theme.palette.primary.main, 0.25) : alpha(theme.palette.primary.main, 0.15),
    '&:hover': {
      backgroundColor: $isDark ? alpha(theme.palette.primary.main, 0.3) : alpha(theme.palette.primary.main, 0.2),
    },
  },
}));

// ============================================================================
// Component
// ============================================================================

export const MagicSelect = forwardRef<HTMLDivElement, MagicSelectProps>(
  (
    { options, label, helperText, error, glowColor = 'primary', glassDropdown = true, fullWidth = true, id, ...props },
    ref,
  ) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const glass = isDark ? glassEffects.dark : glassEffects.light;

    // Generate unique IDs for accessibility
    const generatedId = useId();
    const selectId = id || generatedId;
    const labelId = `${selectId}-label`;

    return (
      <StyledFormControl
        ref={ref}
        fullWidth={fullWidth}
        error={error}
        $isDark={isDark}
        $glowColor={glowColor}
        $error={error}
      >
        {label && (
          <InputLabel id={labelId} title={typeof label === 'string' ? label : undefined}>
            {label}
          </InputLabel>
        )}
        <Select
          id={selectId}
          labelId={label ? labelId : undefined}
          label={label}
          MenuProps={{
            slotProps: {
              paper: {
                sx: glassDropdown
                  ? {
                      background: glass.background,
                      backdropFilter: glass.backdropBlur,
                      border: `1px solid ${glass.border}`,
                      boxShadow: glass.shadow,
                      borderRadius: 2,
                      mt: 1,
                    }
                  : {},
              },
            },
          }}
          {...props}
        >
          {options.map((option) => (
            <StyledMenuItem key={option.value} value={option.value} disabled={option.disabled} $isDark={isDark}>
              {option.label}
            </StyledMenuItem>
          ))}
        </Select>
        {helperText && <FormHelperText error={error}>{helperText}</FormHelperText>}
      </StyledFormControl>
    );
  },
);

MagicSelect.displayName = 'MagicSelect';

export default MagicSelect;
